import React from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import { plainTextFromChildren, renderScopeAwareListItem, renderScopeAwareParagraph, scopeItemTitleMatch } from "./scopeMarkdown";

/**
 * Renders BLU assistant text as Markdown (**bold**, lists, `---`, code fences).
 */
export function BluMarkdown({ children }) {
  const text = String(children ?? "").trim();
  if (!text) return null;

  return (
    <div className="blu-md text-[12px] leading-[1.6] text-inherit [&_a]:text-[#001EFF] [&_a]:underline">
      <ReactMarkdown
        remarkPlugins={[remarkBreaks]}
        components={{
          p: ({ node, children, ...props }) => renderScopeAwareParagraph(children),
          strong: ({ node, children, ...props }) => {
            const t = plainTextFromChildren(children).trim();
            if (scopeItemTitleMatch(t) && !String(t).includes("\n")) {
              return (
                <strong className="font-semibold text-[#001EFF]" {...props}>
                  {children}
                </strong>
              );
            }
            return (
              <strong className="font-semibold" {...props}>
                {children}
              </strong>
            );
          },
          em: ({ node, ...props }) => <em className="italic" {...props} />,
          h1: ({ node, children, ...props }) => {
            const t = plainTextFromChildren(children).trim();
            const accent = scopeItemTitleMatch(t) ? " text-[#001EFF]" : "";
            return <h3 className={`mt-3 mb-1 text-[12px] font-semibold tracking-tight first:mt-0${accent}`} {...props}>{children}</h3>;
          },
          h2: ({ node, children, ...props }) => {
            const t = plainTextFromChildren(children).trim();
            const normalized = t.replace(/\s+/g, " ").toLowerCase();
            const isScopeOfSupport = normalized === "scope of support";
            const base = "mt-2 mb-1.5 text-[12px] font-semibold tracking-tight first:mt-0";
            if (isScopeOfSupport) {
              return (
                <h2
                  className={`${base} uppercase tracking-[0.12em] text-[var(--t-text)] border-0 border-b border-[#E8E4DC] pb-2 mb-2`}
                  {...props}
                >
                  {children}
                </h2>
              );
            }
            const accent = scopeItemTitleMatch(t) ? " text-[#001EFF]" : "";
            return (
              <h2 className={`${base} text-[var(--t-text)]${accent}`} {...props}>
                {children}
              </h2>
            );
          },
          h3: ({ node, children, ...props }) => {
            const t = plainTextFromChildren(children).trim();
            const accent = scopeItemTitleMatch(t) ? " text-[#001EFF]" : "";
            return <h3 className={`mt-3 mb-1 text-[12px] font-semibold tracking-tight first:mt-0${accent}`} {...props}>{children}</h3>;
          },
          ul: ({ node, ...props }) => <ul className="mb-2 list-disc space-y-1 pl-4 last:mb-0" {...props} />,
          ol: ({ node, ...props }) => <ol className="mb-2 list-decimal space-y-1 pl-4 last:mb-0" {...props} />,
          li: ({ node, children, ...props }) => renderScopeAwareListItem(children),
          hr: () => <hr className="my-3 border-0 border-t border-[#E8E4DC]" />,
          blockquote: ({ node, ...props }) => (
            <blockquote className="my-2 border-l-2 border-[#E8E4DC] pl-3 text-[var(--t-meta)]" {...props} />
          ),
          code({ inline, className, children, ...props }) {
            if (inline) {
              return (
                <code
                  className="rounded bg-[rgba(0,0,0,0.06)] px-1 py-0.5 font-mono text-[12px]"
                  {...props}
                >
                  {children}
                </code>
              );
            }
            return (
              <pre className="my-2 max-h-[min(40vh,320px)] overflow-auto rounded-lg bg-[rgba(0,0,0,0.04)] p-3 font-mono text-[12px] leading-relaxed">
                <code className={className} {...props}>
                  {children}
                </code>
              </pre>
            );
          },
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}
